'use strict';

import React from 'react';

import Badge from 'react-bootstrap/Badge';

import TermUtils from '@uw-it-sis/lib-js-common/lib/TermUtils';

import { isFalse } from './Utils';

/*
 * Add in React specific functions to the shared TermUtils object.
 */

/**
 * Makes a term badge (actually a label at the moment).
 *
 * @param term A term description (e.g. "Winter 2018")
 * @returns A Label containing a shortened term description (e.g. "WI 18")
 */
TermUtils.makeTermBadge = function(term, i) {
    let termClass = _getTermClass(term);
    let termLabel = TermUtils._getTermShortName(term);
    return <Badge key={i} bg={termClass} className="text-dark fw-bold"><abbr title={term} aria-hidden={true}>{termLabel}</abbr><span className="sr-only">{term}</span></Badge>;
};


/**
 * Makes a term abbreviation tag from a term description.
 *
 * @param term A term description (e.g. "Winter 2016")
 * @returns An abbr tag containing a shortened term description (e.g. "WI 18")
 */
TermUtils.makeTermAbbr = function(term, i) {
    let termLabel = TermUtils._getTermShortName(term);
    return <abbr key={i} title={term} aria-label={term}>{termLabel.replace(/\s/g,'')}</abbr>;
};

/**
 * Makes a term abbreviation tag from a term ID.
 *
 * @param termId termId An atpId (e.g. "20164")
 * @returns An abbr tag containing a shortened term description (e.g. "WI 18")
 */
TermUtils.makeTermAbbrFromTermId = function(termId, i) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    let termDescription = TermUtils._makeTermDescription(termId);
    let termLabel = TermUtils._getTermShortName(termDescription);
    return <abbr key={i} title={termDescription} aria-label={termDescription}>{termLabel}</abbr>;
};

/**
 * Makes a term React Bootstrap component badge.
 *
 * @param termId An atpId (e.g. "20164")
 * @returns A term badge React component.
 */
TermUtils.makeTermBadgeFromTermId = function(termId, i) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    let termDescription = TermUtils._makeTermDescription(termId);
    return (this.makeTermBadge(termDescription, i));
};

/**
 * Determines the CSS class that should be associated with the term.
 *
 * @param term A string of a term description (e.g. "Winter 2018", "Winter")
 * @returns A string including the term description (minus any year) in lowercase prefixed with 'bg-' to be used as a CSS class name (e.g. "bg-winter")
 * @private
 */
const _getTermClass = function(term) {
    return term.split(" ")[0].toLowerCase();
};

/**
 * Creates an array of terms sorted by the academic year (i.e. autumn, winter, spring, summer)
 * @type {string[]}
 */
TermUtils.sortedList = Object.entries(TermUtils.TERMS).sort((a,b) => {
    if (parseInt(a[0]) === 4) {
        return -1
    } else {
        return a[0]-b[0]
    }
}).map(([id, name], index) => name.toLowerCase());

/**
 * Takes a termId and based on the number of terms calculates a new term id
 * @param termId - starting point from when to calculate the new term id
 * @param terms - number of terms to shift from a given term id
 */
TermUtils.calcNewTermId = (termId, terms) => {
    const termNumber = parseInt(termId.substring(4, 5)); // Get the term number from the 5th character
    const termYear = parseInt(termId.substring(0, 4)); // Get the year from first 4 characters
    const yearShift = Math.floor(Math.abs(terms) / 4);
    const termShift = (Math.abs(terms) % 4);

    let newYear, newTerm;

    if (parseInt(terms) === 0) {
        newYear = termYear;
        newTerm = termNumber;
    } else if (parseInt(terms) > 0) {
        newYear = termYear + yearShift;
        newTerm = termNumber + termShift;
        if (newTerm > 4) {
            newYear++;
            newTerm = newTerm % 4;
        }
    } else if (parseInt(terms) < 0) {
        newYear = termYear - yearShift;
        newTerm = termNumber - termShift;
        if (newTerm < 1) {
            newYear--;
            newTerm = (newTerm % 4) + 4;
        }
    }

    return `${newYear}${newTerm}`;
}

export default TermUtils;